import { STREAM_TYPE_LIVE, STREAM_TYPE_PLAYBACK } from "./base";
import { generateString } from "../common/util";
import { TimeoutWaiter } from "../common/timeout_waiter";
import { AppError } from "../common/app_error";
const OFFER_TYPE_STREAM = 'stream';
const OFFER_TYPE_DATA = 'data';
const OFFER_TYPE_TWO_WAY_AUDIO = 'two-way-audio';
const OFFER_TYPE_GENERAL = 'general';
export class SignalBase {
    constructor(options) {
        // connectId -> listener
        this.listeners = new Map();
        this.debugging = false;
        if (options.debugging)
            this.debugging = true;
        this.waiter = new TimeoutWaiter('SignalBase', SignalBase.SEND_TIMEOUT, this.debugging);
    }
    onMessage(result) {
        if (result?.error) {
            console.error('onMessage: error=%s', JSON.stringify(result.error));
        }
        // callback
        let error = null;
        if (result?.error) {
            error = new AppError(result?.error);
        }
        switch (result?.act) {
            case SignalBase.WS_MSG_DEVICE_CANDIDATES:
                result.data = result?.data?.deviceUserCandidates;
                break;
            case SignalBase.WS_MSG_DEVICE_SNAPSHOTS_REPLY:
                result.data = result?.data?.deviceTakeSnapshotsReply;
                break;
        }
        this.waiter.resetTimeout({ id: result?.id, callbackObject: { error, result } });
        // special actions
        if (result?.act === SignalBase.WS_MSG_DEVICE_CANDIDATES) {
            this.onIceCandidates(result.data);
        }
        else if (result?.act === SignalBase.WS_MSG_DEVICE_RECONNECT) {
            this.deviceReconnect(result.data.deviceUserReconnect);
        }
    }
    onError(result) {
        const listener = this.listeners.get(result.id);
        if (listener) {
            listener.source.onError(result.error);
        }
    }
    onIceCandidates(data) {
        const listener = this.listeners.get(data.connectId);
        if (listener) {
            const candidates = [];
            data.candidates.forEach((candidate) => {
                if (typeof candidate === 'string')
                    candidates.push(JSON.parse(candidate));
                else
                    candidates.push(candidate);
            });
            listener.source.onIceCandidates(candidates);
        }
    }
    deviceReconnect(data) {
        const listener = this.listeners.get(data.connectId);
        if (listener) {
            listener.source.triggerReconnect();
        }
        else {
            console.error('deviceReconnect: listener not found');
        }
    }
    doSendIceCandidates(args) {
        const msgObj = {
            "request": {
                "id": generateString(31),
                "act": SignalBase.WS_MSG_USER_CANDIDATES,
                "data": {
                    "userDeviceCandidates": {
                        "candidates": args.candidates,
                        "token": args.rtcToken,
                        "connectId": args.connectId
                    },
                },
            },
            'headers': {
                "org_id": args.orgId
            }
        };
        if (this.debugging) {
            console.debug('doSendIceCandidates: message=%s', JSON.stringify(msgObj));
        }
        return this.sendMessage(msgObj);
    }
    sendIceCandidates(source, args) {
        let e = null;
        try {
            e = this.doSendIceCandidates(args);
        }
        catch (e) {
        }
        if (e) {
            source.onError(e);
        }
    }
    doSendOffer(offer, type, args, callback, videoCodec) {
        const msgObj = {
            "request": {
                "id": offer.connectId,
                "act": SignalBase.WS_MSG_DEVICE_CONNECT,
                "data": {
                    "deviceConnect": {
                        "credentialTurn": offer.turn,
                        "offer": offer.offer,
                        "token": offer.rtcToken,
                        "type": type,
                        "connectId": offer.connectId,
                        "videoCodec": videoCodec,
                    },
                },
            },
            'headers': {
                trace: offer.traceHeader && { ...offer.traceHeader },
                "org_id": offer.orgId
            }
        };
        if (type === OFFER_TYPE_STREAM) {
            msgObj.request.data.deviceConnect.streamParam = args;
        }
        if (type === OFFER_TYPE_TWO_WAY_AUDIO) {
            msgObj.request.data.deviceConnect.twoWayAudioParam = args;
        }
        if (this.debugging) {
            console.debug('doSendOffer: message=%s', JSON.stringify(msgObj));
        }
        return this.sendTimeout({ obj: msgObj, callback });
    }
    reconnect(args) {
        const msgObj = {
            "request": {
                "id": generateString(31),
                "act": SignalBase.WS_MSG_USER_RECONNECT,
                "data": {
                    "userReconnect": {
                        "offer": args.sdp,
                        "connectId": args.connectId,
                        "token": args.token
                    },
                },
            },
            'headers': {
                "org_id": args.orgId
            }
        };
        if (this.debugging) {
            console.debug('reconnect: message=%s', JSON.stringify(msgObj));
        }
        var callback = (error, result) => {
            if (this.debugging) {
                console.log('reconnect callback result:', result);
            }
            if (error) {
                //@ts-ignore
                args.source.emitError(error);
            }
            else {
                args.source.onReconnectAnswer(result.data.userReconnectReply);
            }
            args?.cb?.();
        };
        return this.sendTimeout({ obj: msgObj, callback });
    }
    sendOffer(source, offer, type, args, videoCodec) {
        const actId = offer.connectId;
        const obj = { actId, source };
        this.listeners.set(offer.connectId, obj);
        return this.doSendOffer(offer, type, args, (error, result) => {
            if (error) {
                source.onError(error);
            }
            else {
                source.onAnswer(result.data.deviceConnectReply);
            }
        }, videoCodec);
    }
    sendBatchOffer(source, offer, args, videoCodec) {
        const actId = offer.connectId;
        const obj = { actId, source };
        this.listeners.set(actId, obj);
        let e = null;
        try {
            const msgObj = {
                "request": {
                    "id": actId,
                    "act": SignalBase.WS_MSG_USER_BATCH_CONNECT,
                    "data": {
                        "userBatchConnect": {
                            "credentialTurn": offer.turn,
                            "offer": offer.offer,
                            "token": offer.rtcToken,
                            "type": OFFER_TYPE_GENERAL,
                            "connectId": offer.connectId,
                            "videoCodec": videoCodec,
                            "generalParam": args
                        },
                    },
                },
                'headers': {
                    trace: offer.traceHeader && { ...offer.traceHeader },
                    "org_id": offer.orgId
                }
            };
            let callback = (error, result) => {
                if (error) {
                    source.onError(error);
                }
                else {
                    source.onAnswer(result.data.userBatchConnectReply);
                }
            };
            if (this.debugging) {
                console.debug('sendBatchOffer: message=%s', JSON.stringify(msgObj));
            }
            e = this.sendTimeout({ obj: msgObj, callback });
        }
        catch (e) {
        }
        if (e) {
            source.onError(e);
        }
        return e;
    }
    sendLiveOffer(source, offer, args, videoCodec) {
        let e = null;
        try {
            const obj = args;
            obj.streamType = STREAM_TYPE_LIVE;
            e = this.sendOffer(source, offer, OFFER_TYPE_STREAM, obj, videoCodec);
        }
        catch (e) {
        }
        if (e) {
            source.onError(e);
        }
    }
    sendAudioOffer(source, offer, args) {
        let e = null;
        try {
            e = this.sendOffer(source, offer, OFFER_TYPE_TWO_WAY_AUDIO, args);
        }
        catch (e) {
        }
        if (e) {
            source.onError(e);
        }
    }
    sendPlaybackOffer(source, offer, args, videoCodec) {
        let e = null;
        try {
            const obj = args;
            obj.streamType = STREAM_TYPE_PLAYBACK;
            e = this.sendOffer(source, offer, OFFER_TYPE_STREAM, obj, videoCodec);
        }
        catch (e) {
        }
        if (e) {
            source.onError(e);
        }
    }
    sendDataOffer(source, offer) {
        let e = null;
        try {
            e = this.sendOffer(source, offer, OFFER_TYPE_DATA);
        }
        catch (e) {
        }
        if (e) {
            source.onError(e);
        }
    }
    deviceTakeSnapshots(args, callback) {
        let e = null;
        try {
            e = this.doDeviceTakeSnapshots(args, callback);
        }
        catch (e) {
        }
        return e ? AppError.fromError(e) : null;
    }
    doDeviceTakeSnapshots(args, callback) {
        const msgObj = {
            "request": {
                "id": args.actId || generateString(31),
                "act": SignalBase.WS_MSG_DEVICE_SNAPSHOTS,
                "data": {
                    "deviceTakeSnapshots": {
                        "nvrId": args.nvrId,
                        "connectId": args.connectId,
                        "cameras": args.cameras
                    },
                },
            },
            "headers": {
                "orgId": args.orgId
            }
        };
        if (this.debugging) {
            console.debug('doDeviceTakeSnapshots: message=%s', JSON.stringify(msgObj));
        }
        // timeout=15s
        return this.sendTimeout({ obj: msgObj, callback, timeout: args.timeout });
    }
    sendTimeout(options) {
        const request = options.obj.request;
        const error = this.sendMessage(options.obj);
        if (!error) {
            this.waiter.setTimeout({
                id: request.id, callback: options.callback,
                timeout: options.timeout ?? SignalBase.SEND_TIMEOUT
            }, request.act);
        }
        return error;
    }
    detach(source) {
        this.listeners.delete(source.connectId);
    }
}
SignalBase.WS_MSG_DEVICE_CONNECT = "webrtc.user.device.connect";
SignalBase.WS_MSG_USER_BATCH_CONNECT = "webrtc.user.device.batch_connect";
SignalBase.WS_MSG_USER_RECONNECT = "webrtc.user.device.reconnect";
SignalBase.WS_MSG_DEVICE_CONNECT_REPLY = SignalBase.WS_MSG_DEVICE_CONNECT + ".reply";
SignalBase.WS_MSG_DEVICE_CANDIDATES = "webrtc.device.user.candidates";
SignalBase.WS_MSG_DEVICE_RECONNECT = "webrtc.device.user.reconnect";
SignalBase.WS_MSG_USER_CANDIDATES = "webrtc.user.device.candidates";
SignalBase.WS_MSG_DEVICE_SNAPSHOTS = "device.take_snapshots";
SignalBase.WS_MSG_DEVICE_SNAPSHOTS_REPLY = SignalBase.WS_MSG_DEVICE_SNAPSHOTS + ".reply";
// public static readonly WS_MSG_ACTS = new Set([SignalBase.WS_MSG_DEVICE_CONNECT_REPLY,
//    SignalBase.WS_MSG_DEVICE_CANDIDATES, SignalBase.WS_MSG_DEVICE_SNAPSHOTS_REPLY])
SignalBase.SEND_TIMEOUT = 10000;
